import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShoppingBag, Loader2, ExternalLink, Package,
  ChevronRight, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { resolveMediaUrl } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WOO_SHOP_URL = 'https://miselgroup.com/shop-miselgroup';

export default function ShopPage() {
  const { i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState(null);
  const isEl = i18n.language === 'el';

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/shop/products`;
      if (selectedCategory) {
        url += `?category=${selectedCategory}`;
      }
      const res = await axios.get(url);
      setProducts(res.data.products || []);
      setCategories(res.data.categories || []);
      setError(null);
    } catch (e) {
      console.error('Failed to load products:', e);
      const message = e.response?.status === 404
        ? (isEl ? 'Η σύνδεση shop δεν είναι ακόμη διαθέσιμη εδώ.' : 'Shop backend endpoint is not available yet.')
        : (e.response?.data?.detail || (isEl ? 'Σφάλμα φόρτωσης προϊόντων.' : 'Failed to load products.'));
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openInWooCommerce = (productSlug) => {
    window.open(`${WOO_SHOP_URL}/product/${productSlug}`, '_blank');
  };

  const addToCart = async (productId) => {
    // Cart bridge: redirect to WooCommerce with product
    toast.info(isEl ? 'Μεταφορά στο κατάστημα...' : 'Redirecting to shop...');
    window.open(`${WOO_SHOP_URL}/?add-to-cart=${productId}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-misel-dark">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary/10 to-transparent py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {isEl ? 'Επαγγελματικά Προϊόντα' : 'Professional Products'}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {isEl 
              ? 'Ανακαλύψτε τα προϊόντα που χρησιμοποιούμε στα σεμινάριά μας' 
              : 'Discover the products we use in our seminars'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Shop Notice */}
        {error && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 mb-6 text-sm">
            {error}
          </div>
        )}
        <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag className="text-primary" size={24} />
            <div>
              <p className="text-white font-medium">
                {isEl ? 'Αγορές μέσω του επίσημου καταστήματος' : 'Shop through our official store'}
              </p>
              <p className="text-sm text-gray-400">
                {isEl ? 'Η παραγγελία ολοκληρώνεται στο miselgroup.com' : 'Checkout completed at miselgroup.com'}
              </p>
            </div>
          </div>
          <Button
            onClick={() => window.open(WOO_SHOP_URL, '_blank')}
            className="bg-primary hover:bg-primary/90"
          >
            {isEl ? 'Επισκεφθείτε το Κατάστημα' : 'Visit Shop'}
            <ExternalLink size={16} className="ml-2" />
          </Button>
        </div>

        {/* Categories Filter */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                !selectedCategory 
                  ? 'bg-primary text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {isEl ? 'Όλα' : 'All'}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id 
                    ? 'bg-primary text-white' 
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="mx-auto mb-4 text-gray-500" size={48} />
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEl ? 'Σύντομα διαθέσιμα προϊόντα' : 'Products coming soon'}
            </h2>
            <p className="text-gray-400 mb-6">
              {isEl 
                ? 'Επισκεφθείτε το επίσημο κατάστημά μας για πλήρη επιλογή' 
                : 'Visit our official store for full selection'}
            </p>
            <Button
              onClick={() => window.open(WOO_SHOP_URL, '_blank')}
              className="bg-primary hover:bg-primary/90"
            >
              {isEl ? 'Πήγαινε στο Κατάστημα' : 'Go to Shop'}
              <ExternalLink size={16} className="ml-2" />
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product) => (
              <div 
                key={product.id}
                className="bg-misel-card border border-white/10 rounded-sm overflow-hidden hover:border-primary/50 transition-colors group"
              >
                {product.image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={resolveMediaUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.on_sale && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded">
                        SALE
                      </span>
                    )}
                  </div>
                )}
                
                <div className="p-4">
                  <h3 className="text-white font-medium mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  
                  {product.short_description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2" 
                       dangerouslySetInnerHTML={{ __html: product.short_description }} 
                    />
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      {product.on_sale ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-primary">€{product.sale_price}</span>
                          <span className="text-sm text-gray-500 line-through">€{product.regular_price}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold">€{product.price}</span>
                      )}
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={() => addToCart(product.id)}
                      className="bg-primary hover:bg-primary/90"
                    >
                      <ShoppingBag size={14} className="mr-1" />
                      {isEl ? 'Αγορά' : 'Buy'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
